import React, { useState, useEffect } from "react";
import {
  Card,
  CardBody,
  CardHeader,
} from "../../../../_metronic/_partials/controls";
import {
  Button,
  TextField,
  MuiThemeProvider,
  FormControlLabel,
  Checkbox,
  createTheme,
  Typography,
} from "@material-ui/core";
import { useHistory, useLocation, useParams } from "react-router-dom";
import {
  deleteContact,
  getContactById,
  postContact,
  responderContact,
  updateContact,
} from "../../../../api/contact";
import { useSkeleton } from "../../../hooks/useSkeleton";
import { alertError, alertSuccess } from "../../../../utils/logger";
import ConfirmDialog from "../../../components/dialogs/ConfirmDialog";
import { Autocomplete } from "@material-ui/lab";
import Editor from "../../../components/editor/Editor";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import { SERVER_URL } from "../../../../api";

// Create theme for delete button (red)
const theme = createTheme({
  palette: {
    secondary: {
      main: "#F64E60",
    },
  },
});

function getEmptyContact() {
  return {
    titulo: "",
    active: true,
  };
}

function congresoEdicionIdString(congresoEdicionId) {
  if (congresoEdicionId == null) return "";
  if (typeof congresoEdicionId === "object" && congresoEdicionId._id != null) {
    return String(congresoEdicionId._id);
  }
  return String(congresoEdicionId);
}

function pickTituloEdicion(titulo) {
  if (titulo == null) return "";
  if (typeof titulo === "string") return titulo.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
  if (typeof titulo === "object") {
    const s = titulo.es || titulo.ca || titulo.en || "";
    return String(s)
      .replace(/<[^>]*>/g, " ")
      .replace(/\s+/g, " ")
      .trim();
  }
  return "";
}

function resolveDocumentoPdfUrl(documentoPdf) {
  const p = String(documentoPdf || "").trim();
  if (!p) return "";
  if (/^https?:\/\//i.test(p)) return p;
  const base = String(SERVER_URL || "").replace(/\/+$/, "");
  return `${base}/${p.replace(/^\/+/, "")}`;
}

function getOrigenFormularioLabel(origen) {
  if (origen === "web_congresos_patrocinio") {
    return "Formulario de interés en patrocinio (web de congresos)";
  }
  if (origen === "web_congresos_envio_trabajo") {
    return "Formulario de envío de trabajo (web de congresos)";
  }
  if (origen === "web_congresos") {
    return "Formulario de contacto de la web de congresos";
  }
  return "";
}

function getCongresoEdicionYearDisplay(contact) {
  if (contact.congresoEdicionYear != null && contact.congresoEdicionYear !== "") {
    return String(contact.congresoEdicionYear);
  }
  const pop = contact.congresoEdicionId;
  if (typeof pop === "object" && pop != null && pop.year != null) {
    return String(pop.year);
  }
  return "";
}

function resolveContactReturnPath(locationState) {
  const st = locationState && typeof locationState === "object" ? locationState : {};
  if (typeof st.returnTo === "string" && st.returnTo.trim()) {
    return st.returnTo.trim();
  }
  if (st.fromVerCongresoEdicionId) {
    return `/ver-congreso-edicion/${st.fromVerCongresoEdicionId}`;
  }
  return "/contacts";
}

export default function EditContactPage() {
  const [contact, setContact] = useState(getEmptyContact());
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const contactId = useParams().id;
  const history = useHistory();
  const location = useLocation();
  const contactReturnPath = resolveContactReturnPath(location.state);
  const goBackFromContact = () => history.push(contactReturnPath);

  const {
    isLoading: isLoadingData,
    disableLoading: disableLoadingData,
    ContentSkeleton,
  } = useSkeleton();

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!contactId) {
          disableLoadingData();
          return;
        }
        getContactById(contactId)
          .then((res) => {
            if (res.status === 200) {
              const user = res.data;
              delete user.password;
              setContact(user);

              disableLoadingData();
            }
          })
          .catch((error) => {
            alertError({
              error: error,
              customMessage: "Could not get contact.",
            });
            history.push("/contacts");
          });
      } catch (error) {}
    };
    fetchData();
  }, [contactId, disableLoadingData, history]);

  function saveContact() {
    let saveContact = contact;
    if (!contactId) {
      postContact(saveContact)
        .then((res) => {
          if (res.status === 201) {
            alertSuccess({
              title: "Saved!",
              customMessage: "Contact successfully created.",
            });
            goBackFromContact();
          }
        })
        .catch((error) => {
          alertError({
            error: error,
            customMessage: "Could not save contact.",
          });
        });
    } else {
      const payload = { ...saveContact };
      if (payload.congresoEdicionId && typeof payload.congresoEdicionId === "object") {
        payload.congresoEdicionId = payload.congresoEdicionId._id || payload.congresoEdicionId;
      }
      updateContact(contactId, payload)
        .then((res) => {
          if (res.status === 200) {
            alertSuccess({
              title: "Saved!",
              customMessage: "Changes successfully saved.",
            });
            goBackFromContact();
          }
        })
        .catch((error) => {
          alertError({
            error: error,
            customMessage: "Could not save changes.",
          });
        });
    }
  }

  const handleChange = (element) => (event) => {
    setContact({ ...contact, [element]: event.target.value });
  };
  const handleChangeEditor = (element, value) => {
    setContact({ ...contact, [element]: value });
  };
  const handleChangeSelect = (element, newValue) => {
    setContact({ ...contact, [element]: newValue });
  };

  const renderStickyButtons = ({
    onBack,
    onSave,
    onDelete,
    setObj,
    obj,
    showDelete = false,
  }) => {
    return (
      <div
        className="sticky-top bg-white border-top shadow-sm"
        style={{
          zIndex: 1000,
          paddingLeft: "15px",
          paddingRight: "15px",
        }}
      >
        <div className="container-fluid">
          <div className="row py-3">
            <div className="col-12">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                {/* Botón Back */}
                <div className="d-flex gap-2">
                  <Button
                    onClick={onBack}
                    variant="outlined"
                    size="medium"
                    className="text-nowrap"
                    startIcon={<ArrowBackIcon />}
                  >
                    Back
                  </Button>
                </div>

                {/* Botones Save y Delete */}
                <div className="d-flex gap-2">
                  <Button
                    onClick={onSave}
                    variant="outlined"
                    color="primary"
                    style={{ marginRight: "20px" }}
                  >
                    Save
                  </Button>

                  {showDelete && (
                    <MuiThemeProvider theme={theme}>
                      <Button
                        onClick={onDelete}
                        variant="outlined"
                        color="secondary"
                        size="medium"
                        className="text-nowrap"
                      >
                        Delete
                      </Button>
                    </MuiThemeProvider>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (isLoadingData) return <ContentSkeleton />;
  else
    return (
      <>
        <Card>
          {renderStickyButtons({
            onBack: goBackFromContact,
            onSave: saveContact,
            onDelete: () => setOpenConfirmDialog(1),
            showDelete: !!contactId,
            setObj: setContact,
            obj: contact,
          })}

          <CardHeader title="Editar Contact"></CardHeader>
          <CardBody>
            {contactId &&
            (contact.origenFormulario === "web_congresos" ||
              contact.origenFormulario === "web_congresos_patrocinio" ||
              contact.origenFormulario === "web_congresos_envio_trabajo") ? (
              <>
                <Typography variant="h6" gutterBottom className="ml-4 mt-2">
                  Origen del mensaje
                </Typography>
                <div className="d-flex flex-wrap">
                  <div className="col-md-12">
                    <TextField
                      id="contact-origen-formulario"
                      label="Origen"
                      value={getOrigenFormularioLabel(contact.origenFormulario)}
                      onChange={() => {}}
                      inputProps={{ readOnly: true }}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      margin="normal"
                      variant="outlined"
                    />
                  </div>
                  <div className="col-md-6">
                    <TextField
                      id="contact-origen-anio"
                      label="Año de la edición"
                      value={getCongresoEdicionYearDisplay(contact) || "—"}
                      onChange={() => {}}
                      inputProps={{ readOnly: true }}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      margin="normal"
                      variant="outlined"
                    />
                  </div>
                  <div className="col-md-6">
                    <TextField
                      id="contact-origen-titulo"
                      label="Congreso"
                      value={
                        typeof contact.congresoEdicionId === "object" &&
                        contact.congresoEdicionId != null
                          ? pickTituloEdicion(contact.congresoEdicionId.titulo) || "—"
                          : "—"
                      }
                      onChange={() => {}}
                      inputProps={{ readOnly: true }}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      margin="normal"
                      variant="outlined"
                    />
                  </div>
                  <div className="col-md-12">
                    <TextField
                      id="contact-origen-mongo"
                      label="Id. Mongo edición"
                      value={congresoEdicionIdString(contact.congresoEdicionId) || "—"}
                      onChange={() => {}}
                      inputProps={{ readOnly: true }}
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      margin="normal"
                      variant="outlined"
                    />
                  </div>
                </div>
                <hr />
              </>
            ) : null}
            <div className="row">
              <div className="col-md-4">
                <TextField
                  id={`name`}
                  label="Nombre"
                  value={contact.name}
                  onChange={handleChange("name")}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  margin="normal"
                  variant="outlined"
                  required
                  fullWidth
                />
              </div>
              <div className="col-md-4">
                <TextField
                  id={`email`}
                  label="Email"
                  value={contact.email}
                  onChange={handleChange("email")}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  margin="normal"
                  variant="outlined"
                  required
                  fullWidth
                />
              </div>
              <div className="col-md-4">
                <TextField
                  id={`tel`}
                  label="Teléfono"
                  value={contact.tel}
                  onChange={handleChange("tel")}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  margin="normal"
                  variant="outlined"
                  fullWidth
                />
              </div>
              {contact.origenFormulario === "web_congresos_envio_trabajo" ? (
                <div className="col-md-6">
                  <TextField
                    id="contact-dni"
                    label="DNI / NIE"
                    value={contact.dni || ""}
                    onChange={handleChange("dni")}
                    InputLabelProps={{
                      shrink: true,
                    }}
                    margin="normal"
                    variant="outlined"
                    fullWidth
                  />
                </div>
              ) : null}
              <div
                className={
                  contact.origenFormulario === "web_congresos_envio_trabajo"
                    ? "col-md-6"
                    : "col-md-4"
                }
              >
                <Autocomplete
                  className="w-100"
                  id="autocomplete-familia"
                  options={["Pendiente", "Gestionado"]}
                  value={contact.gestionado}
                  onChange={(event, newValue) =>
                    handleChangeSelect("gestionado", newValue)
                  }
                  fullWidth
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      margin="normal"
                      variant="outlined"
                      fullWidth
                      InputLabelProps={{
                        shrink: true,
                      }}
                      label="Seleccionar Estado"
                    />
                  )}
                />
              </div>
            </div>
            {contact.origenFormulario === "web_congresos_envio_trabajo" ? (
              <div className="row">
                <div className="col-md-6">
                  <TextField
                    id="contact-titulacion"
                    label="Titulación"
                    value={contact.titulacion || ""}
                    onChange={handleChange("titulacion")}
                    InputLabelProps={{ shrink: true }}
                    margin="normal"
                    variant="outlined"
                    fullWidth
                  />
                </div>
                <div className="col-md-6">
                  <TextField
                    id="contact-situacion-laboral"
                    label="Situación laboral"
                    value={contact.situacionLaboral || ""}
                    onChange={handleChange("situacionLaboral")}
                    InputLabelProps={{ shrink: true }}
                    margin="normal"
                    variant="outlined"
                    fullWidth
                  />
                </div>
                <div className="col-12">
                  <TextField
                    id="contact-centro-trabajo"
                    label="Centro de trabajo"
                    value={contact.centroTrabajo || ""}
                    onChange={handleChange("centroTrabajo")}
                    InputLabelProps={{ shrink: true }}
                    margin="normal"
                    variant="outlined"
                    fullWidth
                  />
                </div>
              </div>
            ) : null}

            <div className="d-flex">
              <div className="col-md-12">
                <TextField
                  id={`asunto`}
                  label="Asunto"
                  value={contact.asunto}
                  onChange={handleChange("asunto")}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  margin="normal"
                  variant="outlined"
                  required
                />
              </div>
            </div>
            {contact.origenFormulario === "web_congresos_envio_trabajo" &&
            contact.documentoPdf ? (
              <div className="d-flex mb-3">
                <div className="col-md-12">
                  <Typography variant="subtitle1" gutterBottom>
                    Documento PDF adjunto
                  </Typography>
                  <Button
                    variant="outlined"
                    color="primary"
                    component="a"
                    href={resolveDocumentoPdfUrl(contact.documentoPdf)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ marginRight: "12px" }}
                  >
                    Ver / descargar PDF
                  </Button>
                  <Typography variant="body2" color="textSecondary" component="p">
                    {contact.documentoPdf}
                  </Typography>
                  <iframe
                    title="Vista previa PDF"
                    src={resolveDocumentoPdfUrl(contact.documentoPdf)}
                    style={{
                      width: "100%",
                      minHeight: "480px",
                      marginTop: "12px",
                      border: "1px solid #e0e0e0",
                      borderRadius: "8px",
                    }}
                  />
                </div>
              </div>
            ) : null}
            <div className="d-flex">
              <div className="col-md-12">
                <label>Mensaje</label>
                <Editor
                  body={contact.mensaje || ""}
                  setBody={(new_body) =>
                    handleChangeEditor("mensaje", new_body)
                  }
                  className="max-height"
                  placeholder={"Enter text mensaje here..."}
                />
              </div>
            </div>
            <hr />
            <Typography variant="h6" gutterBottom className="ml-4">
              Respuesta
            </Typography>
            <div className="d-flex">
              <div className="col-md-12">
                <TextField
                  id={`asunto`}
                  label="Asunto"
                  value={contact.asunto}
                  onChange={handleChange("asunto")}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  margin="normal"
                  variant="outlined"
                  multiline
                  required
                />
              </div>
            </div>
            <div className="d-flex">
              <div className="col-md-12">
                <Editor
                  body={contact.respuesta || ""}
                  setBody={(new_body) =>
                    handleChangeEditor("respuesta", new_body)
                  }
                  className="max-height"
                  placeholder="Enter text here..."
                />
              </div>
            </div>
            <div className="d-flex justify-content-end">
              <Button
                onClick={() => {
                  setOpenConfirmDialog(2);
                }}
                variant="outlined"
                color="primary"
                className="mt-2 mr-4"
              >
                Contestar
              </Button>
            </div>

            {/* Checkboxes */}
            <div style={{ marginTop: "20px" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={contact.active || false}
                    onChange={() =>
                      setContact({ ...contact, active: !contact.active })
                    }
                    name="checkActive"
                  />
                }
                label="Active"
              />
            </div>
          </CardBody>
        </Card>

        {contactId && (
          <>
            <ConfirmDialog
              title={"Are you sure you want to delete this contact?"}
              open={openConfirmDialog === 1}
              setOpen={setOpenConfirmDialog}
              onConfirm={() => {
                deleteContact(contactId)
                  .then((res) => {
                    if (res.status === 204 || res.status === 200) {
                      alertSuccess({
                        title: "Deleted!",
                        customMessage: "Contact deleted successfully",
                      });
                      goBackFromContact();
                    }
                  })
                  .catch((error) => {
                    alertError({
                      error: error,
                      customMessage: "Could not delete contact.",
                    });
                  });
              }}
            />
            <ConfirmDialog
              title={"Are you sure you want to answer this contact?"}
              open={openConfirmDialog === 2}
              setOpen={setOpenConfirmDialog}
              onConfirm={() => {
                const raw = { ...contact };
                if (raw.congresoEdicionId && typeof raw.congresoEdicionId === "object") {
                  raw.congresoEdicionId = raw.congresoEdicionId._id || raw.congresoEdicionId;
                }
                responderContact(contactId, raw)
                  .then((res) => {
                    if (res.status === 200) {
                      alertSuccess({
                        title: "Saved!",
                        customMessage: "Changes successfully saved.",
                      });
                      goBackFromContact();
                    }
                  })
                  .catch((error) => {
                    alertError({
                      error: error,
                      customMessage: "Could not save changes.",
                    });
                  });
              }}
            />
          </>
        )}
      </>
    );
}
